import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { History, Search, User, Clock, Filter, ChevronDown, ChevronUp, Download, RefreshCw, Eye } from 'lucide-react';
import * as XLSX from 'xlsx';

const SystemLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEntity, setFilterEntity] = useState('all');
  const [filterAction, setFilterAction] = useState('all');
  const [pagination, setPagination] = useState({ total: 0, skip: 0, limit: 50 });
  const [expandedLog, setExpandedLog] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  useEffect(() => { fetchData(); }, [pagination.skip]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/audit', { params: { limit: pagination.limit, skip: pagination.skip } });
      setLogs(response.data.logs || []);
      setPagination(prev => ({ ...prev, total: response.data.total || 0 }));
    } catch (error) {
      toast.error('Eroare la incarcare');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    try {
      const exportData = filteredLogs.map(log => ({
        'Data/Ora': new Date(log.created_at).toLocaleString('ro-RO', { 
          day: '2-digit', month: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit', second: '2-digit'
        }),
        'Utilizator': log.user_name || 'Sistem',
        'Actiune': getActionLabel(log.action),
        'Entitate': getEntityLabel(log.entity_type),
        'ID Entitate': log.entity_id || '-',
        'IP Address': log.ip_address || 'N/A',
        'Detalii': JSON.stringify(log.details || {})
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Log-uri Sistem');
      XLSX.writeFile(wb, `loguri_sistem_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Export Excel generat!');
    } catch (error) {
      toast.error('Eroare la export');
    }
  };

  const getActionColor = (action) => {
    const colors = {
      create: 'bg-green-100 text-green-700 border-green-300',
      update: 'bg-blue-100 text-blue-700 border-blue-300',
      delete: 'bg-red-100 text-red-700 border-red-300',
      sign: 'bg-purple-100 text-purple-700 border-purple-300',
      transform: 'bg-orange-100 text-orange-700 border-orange-300',
      movement: 'bg-cyan-100 text-cyan-700 border-cyan-300',
      consume_stock: 'bg-amber-100 text-amber-700 border-amber-300',
      pick: 'bg-pink-100 text-pink-700 border-pink-300'
    };
    return colors[action] || 'bg-slate-100 text-slate-700 border-slate-300';
  };

  const getActionLabel = (action) => {
    const labels = {
      create: 'CREARE',
      update: 'ACTUALIZARE',
      delete: 'STERGERE',
      sign: 'SEMNARE',
      transform: 'TRANSFORMARE',
      movement: 'MISCARE',
      consume_stock: 'CONSUM STOC',
      pick: 'PICKING'
    };
    return labels[action] || action.toUpperCase();
  };

  const getEntityLabel = (entity) => {
    const labels = {
      user: 'Utilizator',
      equipment: 'Echipament',
      ticket: 'Tichet',
      revision: 'Revizie',
      article: 'Articol',
      supplier: 'Furnizor',
      stock: 'Stoc',
      plant: 'Sediu',
      location: 'Locatie',
      procedure: 'Procedura',
      symptom: 'Simptom',
      cause: 'Cauza',
      approval: 'Aprobare',
      material_request: 'Cerere Consumabile'
    };
    return labels[entity] || entity;
  };

  const getActionDescription = (log) => {
    const entity = getEntityLabel(log.entity_type);
    const action = getActionLabel(log.action).toLowerCase();
    const entityId = log.entity_id ? `#${log.entity_id.substring(0, 8)}` : '';
    
    let description = `${action} ${entity} ${entityId}`;
    
    if (log.details) {
      if (log.details.status) description += ` (status: ${log.details.status})`;
      if (log.details.items_count) description += ` (${log.details.items_count} articole)`;
      if (log.details.quantity) description += ` (${log.details.quantity} unitati)`;
    }
    
    return description;
  };

  const entityTypes = [...new Set(logs.map(l => l.entity_type))];
  const actionTypes = [...new Set(logs.map(l => l.action))];

  const filteredLogs = logs.filter(l => {
    const matchesSearch = l.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          getActionDescription(l).toLowerCase().includes(searchTerm.toLowerCase());
    const matchesEntity = filterEntity === 'all' || l.entity_type === filterEntity;
    const matchesAction = filterAction === 'all' || l.action === filterAction;
    return matchesSearch && matchesEntity && matchesAction;
  });

  const formatDateTime = (dateStr) => {
    return new Date(dateStr).toLocaleString('ro-RO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="system-logs-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Log-uri Sistem</h1>
          <p className="text-slate-600 mt-1">Istoric complet actiuni si evenimente in sistem</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Reincarca
          </Button>
          <Button variant="outline" onClick={handleExport}>
            <Download className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-slate-900">{pagination.total}</p>
              <p className="text-sm text-slate-500">Total Evenimente</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{logs.filter(l => l.action === 'create').length}</p>
              <p className="text-sm text-slate-500">Creare</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{logs.filter(l => l.action === 'update').length}</p>
              <p className="text-sm text-slate-500">Actualizare</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-red-600">{logs.filter(l => l.action === 'delete').length}</p>
              <p className="text-sm text-slate-500">Stergere</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Cauta in log-uri..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-12"
          />
        </div>
        <Select value={filterEntity} onValueChange={setFilterEntity}>
          <SelectTrigger className="w-full md:w-48 h-12">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Entitate" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toate Entitatile</SelectItem>
            {entityTypes.map((type) => (
              <SelectItem key={type} value={type}>{getEntityLabel(type)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterAction} onValueChange={setFilterAction}>
          <SelectTrigger className="w-full md:w-48 h-12">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Actiune" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toate Actiunile</SelectItem>
            {actionTypes.map((type) => (
              <SelectItem key={type} value={type}>{getActionLabel(type)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Logs List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052CC]"></div>
        </div>
      ) : filteredLogs.length > 0 ? (
        <div className="space-y-2">
          {filteredLogs.map((log) => (
            <Card key={log.id} className="card-industrial hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0 mt-1">
                      <History className="w-5 h-5 text-slate-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <Badge className={`${getActionColor(log.action)} border font-semibold`}>
                          {getActionLabel(log.action)}
                        </Badge>
                        <Badge variant="outline" className="border-slate-300">
                          {getEntityLabel(log.entity_type)}
                        </Badge>
                      </div>
                      <p className="text-sm font-medium text-slate-700 mb-2">
                        {getActionDescription(log)}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {log.user_name || 'Sistem'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDateTime(log.created_at)}
                        </span>
                        {log.ip_address && (
                          <span className="flex items-center gap-1">
                            IP: {log.ip_address}
                          </span>
                        )}
                      </div>
                      {log.details && Object.keys(log.details).length > 0 && (
                        <div className="mt-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedLog(log);
                              setShowDetailsDialog(true);
                            }}
                            className="text-xs h-7 px-2"
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            Vezi Detalii Tehnice
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          
          {/* Pagination */}
          <div className="flex items-center justify-between pt-4">
            <p className="text-sm text-slate-600">
              Afiseaza {pagination.skip + 1}-{Math.min(pagination.skip + pagination.limit, pagination.total)} din {pagination.total}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPagination(prev => ({ ...prev, skip: Math.max(0, prev.skip - prev.limit) }))}
                disabled={pagination.skip === 0}
              >
                Anterioara
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }))}
                disabled={pagination.skip + pagination.limit >= pagination.total}
              >
                Urmatoarea
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <Card className="card-industrial">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <History className="w-16 h-16 text-slate-300 mb-4" />
            <p className="text-slate-500">Nu exista inregistrari in log</p>
          </CardContent>
        </Card>
      )}

      {/* Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalii Tehnice Eveniment</DialogTitle>
          </DialogHeader>
          
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Actiune</p>
                  <Badge className={getActionColor(selectedLog.action)}>
                    {getActionLabel(selectedLog.action)}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Entitate</p>
                  <p className="font-medium">{getEntityLabel(selectedLog.entity_type)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Utilizator</p>
                  <p className="font-medium">{selectedLog.user_name || 'Sistem'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Data/Ora</p>
                  <p className="font-medium font-mono text-sm">{formatDateTime(selectedLog.created_at)}</p>
                </div>
                {selectedLog.ip_address && (
                  <div>
                    <p className="text-sm text-slate-500">IP Address</p>
                    <p className="font-medium font-mono">{selectedLog.ip_address}</p>
                  </div>
                )}
                {selectedLog.entity_id && (
                  <div>
                    <p className="text-sm text-slate-500">ID Entitate</p>
                    <p className="font-medium font-mono text-xs">{selectedLog.entity_id}</p>
                  </div>
                )}
              </div>
              
              {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-2">Detalii JSON:</p>
                  <pre className="bg-slate-900 text-green-400 p-4 rounded-lg text-xs overflow-x-auto">
                    {JSON.stringify(selectedLog.details, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SystemLogsPage;